import React, { useEffect, useRef, useState } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { OSM, Vector as VectorSource } from "ol/source";
import GeoJSON from "ol/format/GeoJSON";
import { Icon, Style, Stroke, Fill, Text } from "ol/style";
import { fromLonLat } from "ol/proj";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import { get as getProjection } from "ol/proj";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import "../styles/Geothermal.css";

const Zipcode = () => {
  const mapRef = useRef(null);
  const zipLayerRef = useRef(null);
  const countyLayerRef = useRef(null);
  const zipSourceRef = useRef(null);

  const [allZipData, setAllZipData] = useState([]);
  const [zipData, setZipData] = useState([]);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [uniqueZipCount, setUniqueZipCount] = useState(0);

  useEffect(() => {
    if (mapRef.current) return;

    const baseLayer = new TileLayer({ source: new OSM() });

    const countySource = new VectorSource({
      url: "/boundary.geojson",
      format: new GeoJSON(),
      strategy: bboxStrategy,
    });

    const countyLayer = new VectorLayer({
      source: countySource,
      style: (feature) =>
        new Style({
          stroke: new Stroke({ color: "black", width: 2 }),
          fill: new Fill({ color: "rgba(0, 0, 255, 0.1)" }),
          text: new Text({
            font: "bold 12px Arial",
            fill: new Fill({ color: "black" }),
            text: feature.get("name") || "No Name",
          }),
        }),
    });

    countyLayerRef.current = countyLayer;

    const zipSource = new VectorSource({
      url: "/zipCodeArea.geojson",
      format: new GeoJSON(),
      strategy: bboxStrategy,
    });

    const zipLayer = new VectorLayer({
      source: zipSource,
      style: (feature) =>
        new Style({
          stroke: new Stroke({ color: "red", width: 1 }),
          image: new Icon({
            src: "https://openlayers.org/en/latest/examples/data/icon.png",
            scale: 0.1,
            anchor: [0.5, 1],
          }),
          text: new Text({
            font: "bold 10px Arial",
            fill: new Fill({ color: "blue" }),
            text: `${feature.get("ZIP5")} - ${feature.get("NAME")}`,
          }),
        }),
      visible: false,
    });

    const map = new Map({
      target: "map",
      layers: [baseLayer, countyLayer, zipLayer],
      view: new View({
        center: fromLonLat([-111.641, 39.8608]),
        zoom: 6.5,
      }),
    });

    mapRef.current = map;
    zipLayerRef.current = zipLayer;
    zipSourceRef.current = zipSource;

    Promise.all([
      fetch("/boundary.geojson").then((res) => res.json()),
      fetch("/zipCodeArea.geojson").then((res) => res.json()),
    ]).then(([countyData, zipData]) => {
      const countyFeatures = new GeoJSON().readFeatures(countyData, {
        featureProjection: getProjection("EPSG:3857"),
      });

      const zipFeatures = new GeoJSON().readFeatures(zipData, {
        featureProjection: getProjection("EPSG:3857"),
      });

      const mappedZipData = zipFeatures.map((zip) => {
        const zipGeometry = zip.getGeometry();
        let zipCenter;
        if (zipGeometry.getType() === "Polygon") {
          zipCenter = zipGeometry.getInteriorPoint().getCoordinates();
        } else if (zipGeometry.getType() === "MultiPolygon") {
          zipCenter = zipGeometry
            .getPolygons()[0]
            .getInteriorPoint()
            .getCoordinates();
        }

        const countyMatch = countyFeatures.find((county) =>
          county.getGeometry().intersectsCoordinate(zipCenter)
        );

        return {
          zip: zip.get("ZIP5"),
          area: zip.get("NAME"),
          county: countyMatch ? countyMatch.get("name") : "Unknown",
          geometry: zipGeometry.getExtent(),
        };
      });

      setAllZipData(mappedZipData);
      setZipData(
        [...mappedZipData].sort((a, b) => a.area.localeCompare(b.area))
      );

      // ✅ Calculate and store unique ZIP count
      const count = new Set(mappedZipData.map((item) => item.zip)).size;
      setUniqueZipCount(count);
    });

    map.on("click", (event) => {
      map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
        if (layer === countyLayer) {
          setSelectedCounty(feature.get("name"));
          map.getView().fit(feature.getGeometry().getExtent(), {
            duration: 1000,
            padding: [50, 50, 50, 50],
          });
          zipLayer.setVisible(true);
        }
      });
    });
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setZipData(allZipData);
      return;
    }
    const filtered = allZipData.filter(
      (item) =>
        item.zip.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.area.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.county.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setZipData(filtered);
  }, [searchTerm, allZipData]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleRowClick = (item) => {
    if (!mapRef.current || !zipSourceRef.current) return;
    zipLayerRef.current.setVisible(true);

    setTimeout(() => {
      const features = zipSourceRef.current.getFeatures();
      const zipFeature = features.find((f) => f.get("ZIP5") === item.zip);
      if (!zipFeature) return;
      mapRef.current.getView().fit(zipFeature.getGeometry().getExtent(), {
        duration: 1000,
        padding: [50, 50, 50, 50],
      });
    }, 200);
  };

  return (
    <div>
      <LandingSection
        data={{
          imageName: "close-up-magnifying-glass-map.webp",
          title: "Utah Zip Codes",
          visible: "",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah Zip Codes (294)</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah ZIP codes organize the state’s regions for efficient mail
              delivery and reflect geographic and population distribution.
            </p>
          </div>
        </div>
      </div>

      <div className="geothermal-map">
        {/* Left-side list */}
        <div className="list-container">
          <h2 className="text-center">
            Utah Zip Codes (294)
            <br />
            {/* <span className="text-sm font-base">
                      (*Not all stations are marked, only those with fully verified
                      data are displayed.)
                      <div className="flex justify-end">
                        <a
                          target="_blank"
                          href="https://afdc.energy.gov/fuels/electricity-locations#/analyze?region=US-UT"
                          rel="noreferrer"
                        >
                          Charging Station Source
                        </a>
                      </div>
                    </span> */}
          </h2>

          {/* Search */}
          <div className="flex justify-center items-center my-4">
            <input
              type="text"
              placeholder="Search by ZIP, Area, or County..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input p-2 border border-gray-300 rounded-3xl w-[40rem]"
            />
          </div>

          {/* Data Sources */}
          <div className="flex flex-col items-end mb-4 px-4">
            <span className="text-sm font-base mb-2">Data Sources:</span>
            <div className="flex flex-col items-end gap-1">
              <a
                target="_blank"
                href="https://www.unitedstateszipcodes.org/ut/"
                rel="noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                United States Zip Codes
              </a>
              <a
                target="_blank"
                href="https://www.zipcodestogo.com/Utah/"
                rel="noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Zip Codes To Go
              </a>
              <a
                target="_blank"
                href="https://ut.postcodebase.com/"
                rel="noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Postcode Base
              </a>
              <a
                target="_blank"
                href="https://www.ciclt.net/sn/clt/capitolimpact/gw_state.aspx?state=ut&stfips=49&stname=Utah"
                rel="noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Capitol Impact
              </a>
            </div>
          </div>

          {/* Header */}
          <div className="grid grid-cols-3 items-center gap-4 rounded-lg border-pink-700 hover:border-1 border-solid p-4 transition-all duration-200 bg-red-200/50">
            <span className="text-red-800 font-bold">ZIP Code</span>
            <span className="text-red-800 font-bold">Area</span>
            <span className="text-red-800 font-bold">County</span>
          </div>

          {/* Scrollable List */}
          <div className="list-scroll max-h-[680px] overflow-y-auto mt-2">
            <ul className="p-0 m-0 list-none">
              {zipData.map((item, index) => (
                <li
                  key={index}
                  className="list-item"
                  onClick={() => handleRowClick(item)}
                >
                  <div className="grid grid-cols-3 items-center gap-4 text-blue-800 font-bold">
                    <span>{item.zip}</span>
                    <span>{item.area}</span>
                    <span>{item.county}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Map */}
        <div id="map" style={{ flex: 2, height: "100%" }}></div>
      </div>

      <Footer />
    </div>
  );
};

export default Zipcode;
